import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, ArrowUpRight, MapPin } from "@phosphor-icons/react";
import ApplyModal from "../components/ApplyModal";

function renderBody(md = "") {
  const escape = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); out.push(""); continue; }
    if (line.startsWith("### ")) { flushList(); out.push(`<h3>${escape(line.slice(4))}</h3>`); continue; }
    if (line.startsWith("## "))  { flushList(); out.push(`<h2>${escape(line.slice(3))}</h2>`); continue; }
    if (/^[-*]\s/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${escape(line.replace(/^[-*]\s/, ""))}</li>`);
      continue;
    }
    flushList();
    const html = escape(line)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/`([^`]+)`/g, "<code>$1</code>");
    out.push(`<p>${html}</p>`);
  }
  flushList();
  return out.join("\n");
}

export default function Position() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [closed, setClosed] = useState(false);
  const [showApply, setShowApply] = useState(false);

  useEffect(() => {
    api.get(`/positions/${id}`)
      .then(({ data }) => {
        if (data?.archived) { setClosed(true); return; }
        setP(data);
      })
      .catch(() => setMissing(true));
  }, [id]);

  if (missing || closed) {
    return (
      <div className="px-6 md:px-14 py-24 min-h-[60vh]" data-testid={closed ? "position-closed" : "position-missing"}>
        <h1 className="font-display text-4xl text-white">
          {closed ? "This role is no longer open." : "Position not found."}
        </h1>
        {closed && (
          <p className="mt-3 text-[#8E8E93]">This position has been closed and is no longer accepting applications.</p>
        )}
        <Link to="/careers" className="btn-secondary mt-8 inline-flex"><ArrowLeft size={14} /> All open roles</Link>
      </div>
    );
  }
  if (!p) return <div className="px-6 md:px-14 py-24 font-mono text-[11px] text-[#8E8E93]">loading…</div>;

  return (
    <div data-testid="position-page" className="px-6 md:px-14 py-16 md:py-24">
      <Link to="/careers" className="btn-ghost"><ArrowLeft size={14} /> Careers</Link>
      <article className="mt-8 max-w-3xl">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">{p.department}</span>
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">{p.seniority}</span>
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">{p.employment_type}</span>
        </div>
        <h1 className="mt-4 font-display font-bold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.05]">
          {p.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-[#8E8E93] text-sm">
          <span className="flex items-center gap-1.5"><MapPin size={14} weight="duotone" /> {p.location}</span>
          {p.ats_external_id && <span className="font-mono text-xs">ATS: {p.ats_external_id}</span>}
        </div>
        <p className="mt-5 text-[#D7D7D9] text-lg leading-relaxed">{p.summary}</p>

        <button type="button" onClick={() => setShowApply(true)} data-testid="apply-cta" className="btn-primary mt-8 inline-flex">
          Apply <ArrowUpRight size={14} weight="bold" />
        </button>

        <div className="mt-12 ww-article-body" dangerouslySetInnerHTML={{ __html: renderBody(p.description) }} />

        <div className="mt-16 ww-card p-6">
          <div className="text-overline mb-2">◢ Apply directly</div>
          <div className="font-display text-2xl text-white">One short form. No login.</div>
          <p className="mt-2 text-[#8E8E93] text-sm">
            Fill your details here — resume, a few lines about you, and a couple of role-specific questions. We'll email you the second we receive it.
          </p>
          <button type="button" onClick={() => setShowApply(true)} className="btn-primary mt-5 inline-flex">
            Apply <ArrowUpRight size={14} weight="bold" />
          </button>
        </div>
      </article>

      <ApplyModal open={showApply} position={p} onClose={() => setShowApply(false)} />
    </div>
  );
}
